import * as React from 'react';
import { connect } from 'redux-scaffolding-ts';
import { LanguageDto, DropdownLanguagesStore } from '../../stores/configuration/locations/languages-store';
import { nameof } from '../../utils/object';
import { ItemReference } from '../../stores/dataStore';
import { isNullOrWhiteSpaces } from '../../utils/useful-functions';
import { useCallback, useEffect, useState } from 'react';
import TailwindSelect from 'widgets/editors/tailwind-select';
import { useTranslation } from 'react-i18next';

interface TailwindLanguageEditorProps {
  value: ItemReference;
  onChange: (value: ItemReference) => void;
  className?: string;
  nullable?: boolean;
  languages?: DropdownLanguagesStore;
  reloadOnChange?: boolean;
  clearable?: boolean;
  placeholder?: string;
  onlyForTests?: boolean;
  readOnly?: boolean;
  loadDataOnOpen?: boolean;
}

const TailwindLanguageEditor: React.FC<TailwindLanguageEditorProps> = ({
  value,
  onChange,
  className = '',
  nullable = false,
  languages,
  reloadOnChange,
  clearable = true,
  placeholder,
  onlyForTests,
  loadDataOnOpen = false,
  readOnly = false
}) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<ItemReference>(value);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadLanguages = useCallback(
    async (search: string = '') => {
      try {
        setIsLoading(true);
        const result = await languages.getAllAsync({
          searchQuery: '',
          skip: 0,
          take: 100000,
          orderBy: [{ direction: 'Ascending', field: nameof<LanguageDto>('language'), useProfile: false }],
          filter: isNullOrWhiteSpaces(search) ? [] : [`startswith(tolower(${nameof<LanguageDto>('language')}), '${search.toLowerCase()}')`]
        });

        setOptions(
          result.items.map(item => ({
            value: item.id,
            label: item.language
          }))
        );
      } catch (error) {
        console.error('Error loading locations:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [languages]
  );

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      loadLanguages();
    }
  }, [loadDataOnOpen, options.length, loadLanguages]);

  useEffect(() => {
    if (!loadDataOnOpen || (value && options.length === 0)) {
      loadLanguages();
    }
  }, [loadDataOnOpen, value, options.length, loadLanguages]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleChange = (newValue: string | null) => {
    const selected = newValue ? options.find(opt => opt.value === newValue) : null;

    const itemRef = selected ? { id: selected.value, title: selected.label } : null;

    setSelectedValue(itemRef);
    onChange?.(itemRef);
  };

  return (
    <TailwindSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(language => ({
              id: language.value,
              text: language.label
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={selectedValue?.id || ''}
      onChange={handleChange}
      placeholder={placeholder || 'Select an option'}
      clearable
      searchable={!isLoading}
      disabled={readOnly}
      className="w-full"
      onOpenDropDown={onOpenDropDown}
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['languages', DropdownLanguagesStore])(TailwindLanguageEditor);
